import { CountryCode, isEnum } from 'corso-types';
import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import { GeoCountryCodes } from '~/utils/constants';
import { customerInfoSchema } from '~/utils/schemas';
import Combobox from './form-controls/Combobox';
import Input from './form-controls/Input';

type CustomerInfo = z.infer<typeof customerInfoSchema>;
type InfoProp = Omit<Partial<CustomerInfo>, 'address'> & {
  address?: Partial<CustomerInfo['address']> | null;
};

const wellKnownCountries = [CountryCode.US, CountryCode.CA];
const getProvinceOptions = (countryCode?: string | null) => {
  const isCountryCode = isEnum(countryCode, CountryCode);

  if (!isCountryCode || !wellKnownCountries.includes(countryCode)) return [];

  return GeoCountryCodes[countryCode].provinces
    .map((province) => ({
      label: province.name,
      value: province.code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export default function CustomerInfoFields({
  info,
  className,
  omitEmail = false,
  omitAddress = false,
  requirePhone = false,
  countryCodes = Object.values(CountryCode),
}: {
  info: InfoProp;
  className?: string;
  omitEmail?: boolean;
  omitAddress?: boolean;
  requirePhone?: boolean;
  countryCodes?: CountryCode[];
}) {
  const { email, firstName, lastName, address } = info;
  const [selectedCountryCode, setSelectedCountryCode] =
    useState<CountryCode | null>(
      isEnum(address?.countryCode, CountryCode) ? address.countryCode : null,
    );

  const [provinceSelectOptions, setProvinceSelectOptions] = useState<
    { label: string; value: string }[]
  >(getProvinceOptions(address?.countryCode));

  const sortedCountryCodeOptions = useMemo(() => {
    const prioritized: CountryCode[] = [CountryCode.US, CountryCode.CA];

    const prioritizedOptions = prioritized
      .filter((code) => countryCodes.includes(code))
      .map((code) => ({
        label: GeoCountryCodes[code].name,
        value: code,
      }));

    return [
      ...prioritizedOptions,
      // remaining options sorted alphabetically
      ...countryCodes
        .filter((code) => !prioritized.includes(code))
        .map((code) => ({
          label: GeoCountryCodes[code].name,
          value: code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [countryCodes]);

  const onChangeCountryCode = (value?: string) => {
    setSelectedCountryCode(isEnum(value, CountryCode) ? value : null);
    setProvinceSelectOptions(getProvinceOptions(value));
  };

  return (
    <div
      data-testid="customerInfoFields"
      className={twMerge('grid grid-cols-1 gap-4 sm:grid-cols-2', className)}
    >
      {!omitEmail && (
        <Input
          id="email"
          name="email"
          label="Email Address"
          pattern="^(?!\.)(?!.*\.\.)([a-zA-Z0-9_'+\-\.]*)[a-zA-Z0-9_+\-]@([a-zA-Z0-9][a-zA-Z0-9\-]*\.)+[a-zA-Z]{2,}$"
          defaultValue={email ?? ''}
          required
          className="sm:col-span-2"
          autoComplete="email"
          validationMessages={{
            valueMissing: 'Please enter your email address.',
            typeMismatch: 'Please enter a valid email address.',
            patternMismatch: 'Please enter a valid email address.',
          }}
        />
      )}

      <Input
        id="firstName"
        name="firstName"
        label="First Name"
        defaultValue={firstName ?? ''}
        required
        autoComplete="given-name"
        type="text"
      />

      <Input
        id="lastName"
        name="lastName"
        label="Last Name"
        defaultValue={lastName ?? ''}
        required
        autoComplete="family-name"
        type="text"
      />

      {!omitAddress && (
        <>
          <Combobox
            id="countryCode"
            name="address[countryCode]"
            label="Country"
            defaultValue={address?.countryCode ?? ''}
            required
            autoComplete="country"
            className="sm:col-span-2"
            onChange={(value) => onChangeCountryCode(value)}
            options={sortedCountryCodeOptions}
            validationMessages={{
              valueMissing: 'Please select a country.',
            }}
          />

          <Input
            id="line1"
            name="address[line1]"
            label="Address"
            defaultValue={address?.line1 ?? ''}
            required
            autoComplete="address-line1"
            type="text"
            className="sm:col-span-2"
          />

          <Input
            id="line2"
            name="address[line2]"
            label="Apt, suite, etc."
            defaultValue={address?.line2 ?? ''}
            autoComplete="address-line2"
            required={false}
            type="text"
          />

          <Input
            id="city"
            name="address[city]"
            label="City"
            defaultValue={address?.city ?? ''}
            required
            autoComplete="address-level2"
            type="text"
          />

          {provinceSelectOptions.length > 0 ? (
            <Combobox
              id="stateOrProvinceCode"
              name="address[stateOrProvinceCode]"
              label="State or Province"
              defaultValue={address?.stateOrProvinceCode ?? ''}
              // required when Provinces are available
              required
              autoComplete="address-level1"
              key={selectedCountryCode}
              validationMessages={{
                valueMissing: 'Please select a valid state or province.',
              }}
              options={provinceSelectOptions}
            />
          ) : (
            <Input
              id="stateOrProvinceCode"
              name="address[stateOrProvinceCode]"
              label="State or Province Code"
              defaultValue={address?.stateOrProvinceCode ?? ''}
              autoComplete="address-level1"
              type="text"
              description="ON, NY, NSW etc."
              validationMessages={{
                valueMissing:
                  'Please enter a state or province code (e.g. ON, NY, NSW etc.)',
                patternMismatch:
                  'Please enter a valid two or three character state or province code (e.g. ON, NY, NSW etc.)',
              }}
            />
          )}

          <Input
            id="postalCode"
            name="address[postalCode]"
            label="Postal Code"
            defaultValue={address?.postalCode ?? ''}
            required={selectedCountryCode === CountryCode.US}
            autoComplete="postal-code"
            type="text"
          />

          <Input
            id="phone"
            name="address[phone]"
            label="Phone"
            defaultValue={address?.phone ?? ''}
            required={requirePhone}
            autoComplete="tel"
            type="tel"
          />
        </>
      )}
    </div>
  );
}
